import { useCallback, useEffect, useState, useMemo } from "react";
import {
  Toolbar,
  AppBar,
  Box,
  Typography,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import MenuIcon from "@mui/icons-material/Menu";
import { drawerWidth } from "./NavigationDrawer";
import { ReactComponent as Logo } from "./../../../logo.svg";
import TimerIcon from "@mui/icons-material/Timer";
import { useSelector } from "react-redux";
import AddApplicantDialog from "./AddApplicantDialog";
import GradeIcon from "@mui/icons-material/Grade";
import { yellow } from "@mui/material/colors";
import { isLeaderSelector } from "../../../store/applicantsSlice";
import { useHistory } from "react-router-dom";
import service from "../helpers/GifService";
import { useLoading } from "../../../providers/loadingProvider";
import { PRE_PROPOSAL_STATUS, programTypes } from "../Constants";
import { useConfirm } from "../../../Contexts/ConfirmContext/ConfirmService";
import { useAlert } from "../../../Contexts/AlertContext/AlertService";
import { deactivateProposal } from "../helpers/GifService/applicantService";
import {
  daysLeftSelector,
  proposalSelector,
} from "../../../store/proposalSlice";
import { contactSelector } from "../../../store/userSlice";
import ProposalSelector from "./proposalSelector";
import { institutionSelector } from "../../../store/researchauthoritiesSlice";

export const toolbarHeight = 80;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    justifyContent: "flex-end",
    "& > *": {
      marginLeft: theme.spacing(6),
    },
    marginRight: theme.spacing(2),
  },
  appBar: {
    backgroundColor: process.env.REACT_APP_TOOLBAR_COLOR || "#FFF",
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  toolbar: {
    paddingRight: theme.spacing(1),
    minHeight: toolbarHeight,
    backgroundColor: process.env.REACT_APP_TOOLBAR_COLOR || "#FFF",
    color: "black",
  },
  title: {
    position: "absolute",
    left: 0,
    marginLeft: drawerWidth,
  },
  pairsValue: {
    fontWeight: 600,
    marginLeft: 7,
  },
  buttonsWrapper: {
    "& > *": {
      marginLeft: theme.spacing(1),
    },
  },
  addApplicantButton: {
    "&:hover": {
      backgroundColor: theme.palette.gif.green.hover,
    },
    color: theme.palette.gif.green.color,
    backgroundColor: theme.palette.gif.green.background,
  },
  pdfButton: {
    "&:hover": {
      backgroundColor: theme.palette.gif.blue.hover,
    },
    color: theme.palette.gif.blue.color,
    backgroundColor: theme.palette.gif.blue.background,
  },
  deleteButton: {
    "&:hover": {
      backgroundColor: theme.palette.gif.red.hover,
    },
    color: theme.palette.gif.red.color,
    backgroundColor: theme.palette.gif.red.background,
  },
  logo: {
    position: "absolute",
    left: 85,
  },
  preTypo: {
    fontWeight: 600,
    fontSize: 18,
  },
}));

export default function ApplicationBar(props) {
  const classes = useStyles();
  const history = useHistory();
  const loading = useLoading();
  const confirm = useConfirm();
  const alert = useAlert();
  const contact = useSelector(contactSelector);
  const proposal = useSelector(proposalSelector);
  const isLeader = useSelector(isLeaderSelector);
  const daysLeft = useSelector(daysLeftSelector);
  const institution = useSelector(institutionSelector);
  const [registrationFromOpen, setRegistrationFromOpen] = useState(false);
  const [infos, setInfos] = useState([]);
  const needAddApplicant = useMemo(() => {
    return (
      proposal?.programType === programTypes.regularProgram ||
      proposal?.programType === programTypes.CollaborativeTrack
    );
  }, [proposal]);

  const handleCloseRegForm = () => {
    setRegistrationFromOpen(false);
  };

  const { setOpen } = props;

  const onOpen = useCallback(
    (event) => {
      setOpen();
    },
    [setOpen]
  );

  const addApplicant = () => {
    setRegistrationFromOpen(true);
  };

  const onDelete = async () => {
    const res1 = await confirm({
      title: "Delete Proposal",
      description: "Are you sure you want to delete this proposal?",
    });
    if (!res1) return;
    const res2 = await confirm({
      title: "Please Note!",
      description: `After deleting this proposal all the proposal information will be deleted and and cant be restored.
      Sure you want to delete?`,
    });
    if (!res2) return;

    const loadingId = loading.addLoading("Deleting Proposal");
    const proposalId = proposal.id;
    await deactivateProposal({ proposalId });

    loading.removeLoading(loadingId);

    await alert({
      title: "Proposal Deleted",
      description: `The proposal has been delete!`,
    });

    history.push("/");
  };

  const createPDF = async () => {
    const proposalId = proposal.id;
    if (!Boolean(proposalId)) return;
    const loadingId = loading.addLoading("Downloading Draft PDF");
    const header = proposal.eligibilitystatus === null ? "Draft" : "";
    const pdfPath = await service.createProposalPDF({ proposalId, header });
    await service.downloadRawFile({
      path: pdfPath,
      filename: proposal.name,
    });
    loading.removeLoading(loadingId);
  };
  useEffect(() => {
    const infos = [
      {
        key: institution ? "" : "User: ",
        value: institution ? institution.name : contact?.displayname,
      },
      {
        key: "Days Left",
        value: daysLeft.totalDaysLeft,
        icon: TimerIcon,
        tooltip: `Due date: ${daysLeft.endFormatted}`,
      },
    ];
    setInfos(infos);
  }, [contact, daysLeft, institution]);
  const LeaderIcon = () => (
    <Tooltip title="Proposal Leader" placement="bottom">
      <GradeIcon
        style={{
          color: yellow[500],
          backgroundColor: "#a8a8a8",
          borderRadius: "50%",
          padding: 2,
          height: 30,
          width: 30,
        }}
      />
    </Tooltip>
  );

  return (
    <AppBar position="absolute" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          className={classes.menuButton}
          onClick={onOpen}
          size="large"
        >
          <MenuIcon />
        </IconButton>
        <Logo className={classes.logo} onClick={() => history.push("/")} />
        <Box className={classes.root}>
          <ProposalSelector className={classes.title} />
          {proposal?.status === PRE_PROPOSAL_STATUS && (
            <Typography className={classes.preTypo}>Pre Proposal</Typography>
          )}
          {isLeader && <LeaderIcon />}
          {proposal?.generalInfoLink && (
            <a href={proposal?.generalInfoLink} target="_blank" rel="noreferrer"> 
              {proposal?.status === PRE_PROPOSAL_STATUS
                ? "Pre-proposal "
                : "Proposal "}
              Guidelines
            </a>
          )}
          {infos.map((info) => (
            <Tooltip
              title={info.tooltip || ""}
              placement="bottom"
              key={info.key}
            >
              <Box>
                <Typography
                  className={classes.pairsKey}
                  component="span"
                  variant="subtitle2"
                  color="inherit"
                  noWrap
                >
                  {info.key}
                </Typography>
                <Typography
                  className={classes.pairsValue}
                  component="span"
                  color="inherit"
                  noWrap
                >
                  {info.value}
                </Typography>
              </Box>
            </Tooltip>
          ))}
          <Box className={classes.buttonsWrapper}>
            {isLeader &&
              needAddApplicant &&
              proposal.status === PRE_PROPOSAL_STATUS && (
                <Button
                  variant="contained"
                  className={classes.addApplicantButton}
                  onClick={addApplicant}
                  disableElevation
                >
                  Add Applicant
                </Button>
              )}
            <Button
              variant="contained"
              className={classes.pdfButton}
              onClick={createPDF}
              disableElevation
            >
              View PDF
            </Button>
            <Button
              variant="contained"
              color="secondary"
              className={classes.deleteButton}
              disableElevation
              onClick={onDelete}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Toolbar>
      <AddApplicantDialog
        open={registrationFromOpen}
        handleClose={handleCloseRegForm}
      />
    </AppBar>
  );
}
